import { Button, LoadingOverlay, Table } from "@mantine/core"
import { IconArrowBadgeRight, IconPlus } from "@tabler/icons-react"
import { debounce } from "lodash-es"
import { Suspense, lazy, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { CollectionQuery } from "../../../models/collection.model"
import { Permission } from "../../../models/permission.model"
import { Role } from "../../../models/role.model"
import CollectionSelector from "../../../shared/component/collection-selector/components/collection-selector"
import { CollectionSelectorConfig } from "../../../shared/component/collection-selector/model/collection-selector-config"
import EmptyIcon from "../../../shared/component/empty-icon/empty-icon"
import { Pagination } from "../../../shared/component/pagination/pagination"
import {
  useAddAccountRolePermissionsMutation,
  useAddRoleToAccountMutation,
  useLazyGetRolePermissionsQuery,
  useLazyGetUserRolePermissionsQuery,
  useLazyGetRolesQuery,
  useLazyGetUserRolesQuery,
} from "../store/owner.query"
import { useTranslation } from "react-i18next"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"

const ModalCollectionSelector = lazy(
  () =>
    import(
      "../../../shared/component/collection-selector/components/modal-collection-selector"
    ),
)

const OwnerRoleComponent = () => {
  const params = useParams()
  const { t } = useTranslation()
  const [userInfo] = useCurrentUser()
  const [selectedRole, setSelectedRole] = useState<Role>()
  const [roleModalOpen, setRoleModalOpen] = useState<boolean>(false)
  const [checkedPermissions, setCheckedPermissions] = useState<string[]>([])
  const [collectionQuery, setCollectionQuery] = useState<CollectionQuery>({
    skip: 0,
    filter: [[{ field: "protected", value: false, operator: "=" }]],
  })
  const [permissionCollection, setPermissionCollection] =
    useState<CollectionQuery>({
      skip: 0,
      top: 10,
    })
  const [getRoles, roles] = useLazyGetRolesQuery()
  const [getUserRoles, userRoles] = useLazyGetUserRolesQuery()
  const [addRoleToAccount, addRoleResponse] = useAddRoleToAccountMutation()
  const [getRolePermissions, rolePermissions] = useLazyGetRolePermissionsQuery()
  const [getUserRolePermissions, userRolePermissions] =
    useLazyGetUserRolePermissionsQuery()
  const [addAccountRolePermission, addPermissionResponse] =
    useAddAccountRolePermissionsMutation()

  const collectionSelectorModalConfig: CollectionSelectorConfig = {
    multiple: true,
    size: "35%",
    identity: "id",
    visibleColumn: [{ key: "name", name: t("name") }],
    primaryColumn: { key: "name", name: t("name") },
  }

  const title = (
    <div className="w-full h-12 items-center px-4  text-white flex justify-between">
      <div>Permissions</div>
    </div>
  )

  const footer = (
    <div className="flex space-x-4 justify-between mt-4">
      <div className="w-full">
        {" "}
        <Pagination
          total={rolePermissions.data?.count ?? 0}
          pageSize={[10, 15, 20,100]}
          defaultPageSize={10}
          onPaginationChange={(skip: number, top: number) =>
            setPermissionCollection({
              ...permissionCollection,
              skip: skip,
              top: top,
            })
          }
        />
      </div>
      <div>
        <Button
          leftIcon={<IconArrowBadgeRight />}
          size="xs"
          onClick={() =>
            addAccountRolePermission({
              roleId: selectedRole?.id,
              permissions: checkedPermissions,
              accountId: `${params.id}`,
            })
          }
          className="bg-primary  border border-white hover:shadow-lg "
        >
          {t("assign")}
        </Button>
      </div>
    </div>
  )

  useEffect(() => {
    getRoles(collectionQuery)
  }, [collectionQuery, getRoles])

  useEffect(() => {
    getUserRoles(`${params.id}`).then((response) => {
      if (response) {
        setSelectedRole(response?.data?.data[0])
      }
    })
  }, [params?.id, getUserRoles])

  useEffect(() => {
    if (selectedRole) {
      getRolePermissions({
        collection: permissionCollection,
        roleId: selectedRole?.id,
      })
    }
  }, [selectedRole, permissionCollection])

  useEffect(() => {
    if (selectedRole?.id) {
      getUserRolePermissions({
        roleId: selectedRole?.id,
        accountId: `${params.id}`,
      }).then((permissions) => {
        if (permissions?.data) {
          const checked = permissions?.data?.map((permission: Permission) => {
            return permission.id
          })
          setCheckedPermissions(checked)
        }
      })
    }
  }, [selectedRole, params?.id])

  return (
    <div className="w-full flex space-x-2 p-4">
      <div className="w-2/4 h-full">
        <table className="min-w-full divide-y divide-gray-700">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div className="w-full flex gap-3 justify-between items-center">
                  <div>{t("role", { count: 1 })}</div>

                  {userInfo?.role?.key == "super_admin" && (
                    <div>
                      <Button
                        leftIcon={<IconPlus />}
                        size="xs"
                        onClick={() => setRoleModalOpen(true)}
                        className="bg-gray-700  hover:bg-gray-500 hover:text-white"
                      >
                        {t("addRole")}
                      </Button>
                    </div>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {userRoles?.data?.data?.map((role) => {
              return (
                <tr className="my-2" key={role.id}>
                  <td
                    onClick={() => setSelectedRole(role)}
                    className={`cursor-pointer px-6 py-2 whitespace-nowrap text-sm text-gray-300 rounded-lg hover:bg-gray-500  hover:text-white ${
                      selectedRole?.id === role.id
                        ? "bg-gray-700 text-white"
                        : ""
                    }`}
                  >
                    {role.name}
                  </td>
                </tr>
              )
            })}
            {userRoles?.isLoading && (
              <tr>
                {" "}
                <td>
                  <LoadingOverlay visible />
                  <EmptyIcon />
                </td>
              </tr>
            )}
            {userRoles?.data?.data?.length === 0 && (
              <tr>
                <td>
                  <EmptyIcon />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="w-full ">
        {userRolePermissions?.isLoading ? (
          <LoadingOverlay visible />
        ) : (
          <CollectionSelector
            title={title}
            footer={footer}
            config={collectionSelectorModalConfig}
            total={rolePermissions.data?.count}
            items={rolePermissions.data?.data}
            checkedItemsProp={userRolePermissions?.data}
            loading={rolePermissions.isLoading || rolePermissions?.isFetching}
            paginationChange={(skip: number, top: number) =>
              setPermissionCollection({
                ...permissionCollection,
                skip: skip,
                top: top,
              })
            }
            onCheck={(data: any) => {
              const checked = data?.map((permission: Permission) => {
                return permission.id
              })
              setCheckedPermissions(checked)
            }}
            search={debounce((key: string) => {
              setPermissionCollection({
                ...permissionCollection,
                search: key,
                searchFrom: ["name"],
              })
            }, 1000)}
          />
        )}
      </div>
      <Suspense>
        <ModalCollectionSelector
          config={collectionSelectorModalConfig}
          hasSort={true}
          total={roles.data?.count}
          items={roles.data?.data}
          selectedRows={userRoles?.data?.data}
          itemsLoading={roles.isLoading || roles?.isFetching}
          onPaginationChange={(skip: number, top: number) =>
            setCollectionQuery({ ...collectionQuery, skip: skip, top: top })
          }
          onDone={(data) => {
            const selectedId = data.map((role: Role) => {
              return role.id
            })
            addRoleToAccount({
              accountId: `${params.id}`,
              roles: selectedId,
            })
          }}
          setModalOpened={(data) => {
            setRoleModalOpen(data)
          }}
          modalOpened={roleModalOpen}
          search={debounce((key: string) => {
            setCollectionQuery({
              ...collectionQuery,
              search: key,
              searchFrom: ["name"],
            })
          }, 1000)}
        />
      </Suspense>
    </div>
  )
}
export default OwnerRoleComponent
